import { ValueObject } from "./ValueObject";

const clamp = (min: number, max: number) => (v: number) => {
  if (v < min) {
    return min;
  } else if (v > max) {
    return max;
  } else {
    return v;
  }
}

export interface DiffResult<T extends ValueObject<T>> {
  diff: { x: number, y: number, v: T }[]
}

export class BitMap<T extends ValueObject<T>> implements ValueObject {

  public values: T[];

  constructor(
    public readonly width: number,
    public readonly height: number,
    fv: T
  ) {
    if (width <= 0 || height <= 0) {
      throw new Error(`非法的宽高`);
    }
    this.values = new Array(width * height).fill(0).map(() => fv.clone() as T);
  }

  /**
   * 填充并返回一个新的bitMap
   * @param v 被填充的值
   * @pure 这是一个纯函数
   */
  fill(v: T): BitMap<T> {
    return new BitMap(this.width, this.height, v.clone());
  }

  /**
   * 更新bitmap的宽高并返回一个新的bitmap
   * @param fillValue 填充的值
   * @param width 新的宽度
   * @param height 新的高度
   * @pure 这是一个纯函数
   */
  updateSize(fillValue: T, width: number = this.width, height: number = this.height): BitMap<T> {
    if (width === this.width && height === this.height) {
      return this.clone();
    }
    const m = new BitMap<T>(width, height, fillValue);
    for (let y = 0; y < this.height; y++)
      for (let x = 0; x < this.width; x++) {
        if (x < m.width && y < m.height) {
          m.put(x, y, this.get(x, y))
        }
      }
    return m;
  }


  /**
   * 
   * @param x 
   * @param y 
   * @pure 这是一个纯函数
   */
  get(x: number, y: number): T {
    if (x >= 0 && y >= 0 && x < this.width && y < this.height) {
      // if (this.values[x * this.width + y] === undefined) {
      //   console.log(x * this.width + y)
      //   console.log(this.values.length)
      //   throw new Error(`undefined 的value:${x},${y},${this.values.length},${this.width},${this.height}}`)
      // }
      return this.values[y * this.width + x].clone() as T;
    } else {
      throw Error(`x:${x},y:${y} 越BitMap下标`);
    }
  }

  /**
   * 
   * @param x 
   * @param y 
   * @param v 
   * @IO 这是一个IO函数
   */
  put(x: number, y: number, v: T): BitMap<T> {
    if (x >= 0 && y >= 0 && x < this.width && y < this.height) {
      this.values[y * this.width + x] = v.clone() as T;
      return this;
    } else {
      throw Error(`x:${x},y:${y} 越BitMap下标`);
    }
  }

  /**
   * 返回一个新的，被填充了矩形填充物的，BitMap
   * @param x0 矩形
   * @param y0 矩形
   * @param x1 矩形
   * @param y1 矩形
   * @param v 填充物
   * @Pure 这是一个纯函数
   */
  fillRect(x0: number, y0: number, x1: number, y1: number, v: T): BitMap<T> {
    if (x0 > x1 || y0 > y1) {
      throw new Error("不支持坐标反向");
    }
    const self = this.clone();
    const cwl = clamp(0, self.width);
    const chl = clamp(0, self.height);
    const [sx, sy, ex, ey] = [cwl(x0), chl(y0), cwl(x1), chl(y1)];
    for (let x = sx; x < ex; x++)
      for (let y = sy; y < ey; y++) {
        self.put(x, y, v);
      }
    return self;
  }

  /**
   * 填充封闭区域
   * @param x 
   * @param y 
   * @param v 
   * @Pure 这是一个纯函数
   */
  fillArea(x: number, y: number, fitV: T): BitMap<T> {
    const self = this.clone();
    // 初始化
    const aimC = self.get(x, y);
    if (aimC.eq(fitV)) {
      return self;
    }
    const queue = [[x, y]];
    const vist = {
      [[x, y].join(',')]: true
    };
    // 广度优先遍历
    while (queue.length > 0) {
      const p = queue.shift();
      const [_x, _y] = p!;
      if (self.get(_x, _y).eq(fitV)) {
        continue;
      } else {
        self.put(_x, _y, fitV)
      }
      const willPoints = [
        [_x, _y + 1],
        [_x + 1, _y],
        [_x, _y - 1],
        [_x - 1, _y]
      ]
        .filter(([a, b]) =>
          a >= 0
          && b >= 0
          && a < self.width
          && b < self.height
          && self.get(a, b).eq(aimC)
          && vist[[a, b].join(',')] === undefined
        );
      willPoints
        .forEach(pit => {
          // const [_x, _y] = pit;
          // self.put(_x, _y, fitV);
          vist[pit.join(',')] = true;
          queue.push(pit);
        });
    }
    // 返回一个新的
    return self;
  }

  diff(v: BitMap<T>): DiffResult<T> {
    if (!this.shapeEq(v)) {
      throw new Error('shape不同的bitMap无法diff');
    }
    const result: DiffResult<T> = { diff: [] };
    for (let x = 0; x < this.width; x++)
      for (let y = 0; y < this.height; y++) {
        if (!this.get(x, y).eq(v.get(x, y))) {
          result.diff.push({ x, y, v: this.get(x, y).clone() });
        }
      }
    return result;
  }

  private shapeEq(v: BitMap<T>): boolean {
    return v.width === this.width && v.height === this.height
  }

  /**
   * 
   * @param v 
   * @Pure 这是一个纯函数
   */
  eq(v: BitMap<T>): boolean {
    if (!this.shapeEq(v)) {
      return false;
    }
    for (let x = 0; x < this.width; x++)
      for (let y = 0; y < this.height; y++) {
        if (!this.get(x, y).eq(v.get(x, y))) {
          return false;
        }
      }
    return true;
  }

  /**
   * @Pure 这是一个纯函数
   */
  clone(): BitMap<T> {
    const m = new BitMap<T>(this.width, this.height, this.values[0])
    for (let x = 0; x < this.width; x++)
      for (let y = 0; y < this.height; y++) {
        m.put(x, y, this.get(x, y))
      }
    return m;
  }
}